// src/components/reports/preview/ScheduleReportPreview.js

import React from 'react';
import { useTranslation } from 'react-i18next';
import { ScheduleReportModel } from '../../../models/ScheduleReportModel';

// ─────────────────────────────────────────────────────────────────────────────
// Weekly Summary Helper - Builds one aggregated row per staff per ISO week
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Groups raw shift entries into one summary row per staff per ISO week.
 * Uses getWeekKey() for grouping to correctly handle cross-year boundaries.
 * Sorted by weekKey ascending, then staffName ascending within each week.
 *
 * @param {ScheduleReportModel[]} data
 * @returns {Array<{ staffId, staffName, weekKey, weekNumber, weekYear, totalMinutes }>}
 */
const buildWeeklySummaryRows = (data) => {
  const groupMap = {};

  data.forEach((entry) => {
    const key = `${entry.staffId}-${entry.getWeekKey()}`;
    if (!groupMap[key]) {
      groupMap[key] = {
        staffId: entry.staffId,
        staffName: entry.staffName,
        weekKey: entry.getWeekKey(),
        weekNumber: entry.weekNumber,
        weekYear: entry.weekYear,
        totalMinutes: 0,
      };
    }
    groupMap[key].totalMinutes += (entry.totalMinutes || 0);
  });

  return Object.values(groupMap).sort((a, b) => {
    const weekCompare = a.weekKey.localeCompare(b.weekKey);
    if (weekCompare !== 0) return weekCompare;
    return a.staffName.localeCompare(b.staffName);
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// Schedule Overview - One row per shift entry, sorted by staff then date
// ─────────────────────────────────────────────────────────────────────────────

const ScheduleOverviewView = ({ data, isPreview, previewLimit, t }) => {
  const sorted = [...data].sort((a, b) => {
    const nameCompare = a.staffName.localeCompare(b.staffName);
    if (nameCompare !== 0) return nameCompare;
    return a.date.localeCompare(b.date);
  });

  const displayData = isPreview ? sorted.slice(0, previewLimit) : sorted;

  const totalMinutes = data.reduce(
    (sum, entry) => sum + (entry.totalMinutes || 0),
    0
  );

  return (
    <div className="report-preview schedule-overview-preview">
      <div className="report-preview-scroll">
        <table className="report-table">
          <thead>
            <tr>
              <th className="date-col">{t('date')}</th>
              <th className="staff-col">{t('staff')}</th>
              <th className="time-col">{t('shiftStart')}</th>
              <th className="time-col">{t('shiftEnd')}</th>
              <th className="duration-col">{t('scheduledHours')}</th>
            </tr>
          </thead>

          <tbody>
            {displayData.map((entry, index) => (
              <tr
                key={`${entry.staffId}-${entry.date}-${index}`}
                className={index % 2 === 1 ? 'alternate-row' : ''}
              >
                <td>{entry.getFormattedDate()}</td>
                <td>{entry.staffName}</td>
                <td>{entry.startTime}</td>
                <td>{entry.endTime}</td>
                <td>{entry.getFormattedDuration()}</td>
              </tr>
            ))}

            <tr className="totals-row">
              <td colSpan={4}>
                <strong>{t('total')}</strong>
              </td>
              <td>
                <strong>
                  {ScheduleReportModel.formatDuration(totalMinutes)}
                </strong>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {isPreview && data.length > previewLimit && (
        <div className="preview-more-message">
          {t('andMoreEntries', { count: data.length - previewLimit })}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Weekly Summary - One aggregated row per staff per ISO week
// ─────────────────────────────────────────────────────────────────────────────

const WeeklySummaryView = ({ data, isPreview, previewLimit, t }) => {
  const summaryRows = buildWeeklySummaryRows(data);
  const displayRows = isPreview ? summaryRows.slice(0, previewLimit) : summaryRows;

  const grandTotalMinutes = summaryRows.reduce(
    (sum, row) => sum + (row.totalMinutes || 0),
    0
  );

  return (
    <div className="report-preview weekly-summary-preview">
      <div className="report-preview-scroll">
        <table className="report-table">
          <thead>
            <tr>
              <th className="week-col">{t('calendarWeek')}</th>
              <th className="staff-col">{t('staff')}</th>
              <th className="duration-col">{t('scheduledHours')}</th>
            </tr>
          </thead>

          <tbody>
            {displayRows.map((row, index) => (
              <tr
                key={`${row.staffId}-${row.weekKey}`}
                className={index % 2 === 1 ? 'alternate-row' : ''}
              >
                <td>{t('weekLabel', { week: row.weekNumber })}</td>
                <td>{row.staffName}</td>
                <td>{ScheduleReportModel.formatDuration(row.totalMinutes)}</td>
              </tr>
            ))}

            <tr className="totals-row">
              <td colSpan={2}>
                <strong>{t('total')}</strong>
              </td>
              <td>
                <strong>
                  {ScheduleReportModel.formatDuration(grandTotalMinutes)}
                </strong>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {isPreview && summaryRows.length > previewLimit && (
        <div className="preview-more-message">
          {t('andMoreEntries', { count: summaryRows.length - previewLimit })}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Export
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Preview component for Schedule report types.
 * Handles both Schedule Overview (one row per shift) and
 * Weekly Summary (aggregated totals per staff per ISO week).
 *
 * Props are passed from ReportPreview.js - this component does not
 * read from context directly.
 *
 * @param {ScheduleReportModel[]} props.data - Array of ScheduleReportModel instances
 * @param {string} props.reportType - 'Schedule Overview' or 'Weekly Summary'
 * @param {boolean} props.isPreview - Whether to limit the displayed rows
 * @param {number} props.previewLimit - Maximum rows shown in preview mode
 */
const ScheduleReportPreview = ({
  data,
  reportType,
  isPreview = true,
  previewLimit = 10,
}) => {
  const { t } = useTranslation('reports');

  if (reportType === 'Weekly Summary') {
    return (
      <WeeklySummaryView
        data={data}
        isPreview={isPreview}
        previewLimit={previewLimit}
        t={t}
      />
    );
  }

  return (
    <ScheduleOverviewView
      data={data}
      isPreview={isPreview}
      previewLimit={previewLimit}
      t={t}
    />
  );
};

export default ScheduleReportPreview;